import { supabase } from '../../config/supabase';
import { MonthlyStats } from '../../types/property';
import { updatePropertyStats } from './propertyStatsUpdater';

export const getMonthlyStats = async (propertyId: string): Promise<MonthlyStats[]> => {
  const { data, error } = await supabase
    .from('monthly_stats')
    .select('*')
    .eq('property_id', propertyId)
    .order('year', { ascending: false })
    .order('month', { ascending: false });

  if (error) throw error;
  
  return data.map(stat => ({
    id: stat.id,
    propertyId: stat.property_id,
    year: stat.year,
    month: stat.month,
    revenue: Number(stat.revenue),
    cleaningCosts: Number(stat.cleaning_costs),
    inmohostCosts: Number(stat.inmohost_costs),
    profit: Number(stat.profit),
    occupancyRate: Number(stat.occupancy_rate),
    reservations: Number(stat.reservations),
    averageNightPrice: Number(stat.average_night_price),
    averageStayDuration: Number(stat.average_stay_duration),
    created_at: stat.created_at
  }));
};

export const createMonthlyStats = async (stats: Omit<MonthlyStats, 'id' | 'created_at'>): Promise<MonthlyStats> => {
  const { data, error } = await supabase
    .from('monthly_stats')
    .insert([{
      property_id: stats.propertyId,
      year: stats.year,
      month: stats.month,
      revenue: stats.revenue,
      cleaning_costs: stats.cleaningCosts,
      inmohost_costs: stats.inmohostCosts,
      profit: stats.profit,
      occupancy_rate: stats.occupancyRate,
      reservations: stats.reservations,
      average_night_price: stats.averageNightPrice,
      average_stay_duration: stats.averageStayDuration
    }])
    .select()
    .single();

  if (error) throw error;

  await updatePropertyStats(stats.propertyId);
  
  return {
    id: data.id,
    propertyId: data.property_id,
    year: data.year,
    month: data.month,
    revenue: Number(data.revenue),
    cleaningCosts: Number(data.cleaning_costs),
    inmohostCosts: Number(data.inmohost_costs),
    profit: Number(data.profit),
    occupancyRate: Number(data.occupancy_rate),
    reservations: Number(data.reservations),
    averageNightPrice: Number(data.average_night_price),
    averageStayDuration: Number(data.average_stay_duration),
    created_at: data.created_at
  };
};

export const updateMonthlyStats = async (id: string, stats: Partial<MonthlyStats>): Promise<void> => {
  const { error } = await supabase
    .from('monthly_stats')
    .update({
      year: stats.year,
      month: stats.month,
      revenue: stats.revenue,
      cleaning_costs: stats.cleaningCosts,
      inmohost_costs: stats.inmohostCosts,
      profit: stats.profit,
      occupancy_rate: stats.occupancyRate,
      reservations: stats.reservations,
      average_night_price: stats.averageNightPrice,
      average_stay_duration: stats.averageStayDuration
    })
    .eq('id', id);

  if (error) throw error;

  if (stats.propertyId) {
    await updatePropertyStats(stats.propertyId);
  }
};

export const deleteMonthlyStats = async (id: string, propertyId: string): Promise<void> => {
  const { error } = await supabase
    .from('monthly_stats')
    .delete()
    .eq('id', id);

  if (error) throw error;

  await updatePropertyStats(propertyId);
};